import {UploadError} from '@/types/file';
import {RequestError} from '@/services/request';
import {PollingManager} from '@/polling/pollingManager';
import {inferUploadFileType, resolveMaxSizeByte} from '@/utils/validators';
import type {
    UploadOptions,
    UploadFileType,
    UploadFileTypeOption,
    UploadSource,
    UploadResult,
    UploadFileItem,
    ChunkInitData,
    RiskStatusResult,
} from '@/types/file';
import type {ProjectId} from '@/types';
import {
    directUploadFile,
    getUploadId,
    uploadChunkPart,
    mergeChunkParts,
    queryRiskStatus,
} from '@/services/upload';

// ============================================================
// 按文件类型的默认配置
// ============================================================

const MB = 1024 * 1024;
const RISK_POLLING_INTERVAL = 3000;
const RISK_POLLING_MAX_RETRIES = 3;

/** 计算上传进度百分比（0-100 整数） */
export const calcProgress = (completed: number, total: number): number =>
    total > 0 ? Math.round((completed / total) * 100) : 0;

let fileIdCounter = 0;
/** 生成全局唯一文件 ID（用于上传项追踪） */
export const generateFileId = (): string => `upload_${Date.now()}_${++fileIdCounter}`;

/** 根据上传响应构造成功态 UploadFileItem */
export const buildSuccessItem = (item: UploadFileItem, response: UploadResult): UploadFileItem => ({
    ...item,
    progress: 100,
    status: 'SUCCESS',
    response,
    riskStatus: response.riskStatus,
    taskId: response.taskId,
    coverUrl: response.coverUrl,
    error: undefined,
});

const FILE_TYPE_PROTOCOL_MAP: Record<UploadFileType, string> = {
    IMAGE: 'image',
    VIDEO: 'video',
    AUDIO: 'audio',
    TXT: 'txt',
};

export const DEFAULT_UPLOAD_CONFIGS: Record<UploadFileType, Required<UploadOptions>> = {
    IMAGE: {
        maxSizeByte: 10 * MB,
        allowedExtensions: ['jpg', 'jpeg', 'png', 'webp'],
        maxCount: 1,
        multiple: false,
        concurrency: 1,
        customUrl: '',
        transport: 'direct',
        chunkThreshold: 10 * MB,
        chunkSize: 5 * MB,
        chunkConcurrency: 3,
        maxChunkRetry: 2,
    },
    VIDEO: {
        maxSizeByte: 500 * MB,
        allowedExtensions: ['mp4', 'mov', 'avi', 'mkv', 'webm'],
        maxCount: 1,
        multiple: false,
        concurrency: 1,
        customUrl: '',
        transport: 'auto',
        chunkThreshold: 30 * MB,
        chunkSize: 10 * MB,
        chunkConcurrency: 3,
        maxChunkRetry: 2,
    },
    AUDIO: {
        maxSizeByte: 50 * MB,
        allowedExtensions: ['mp3', 'wav'],
        maxCount: 1,
        multiple: false,
        concurrency: 1,
        customUrl: '',
        transport: 'direct',
        chunkThreshold: 10 * MB,
        chunkSize: 5 * MB,
        chunkConcurrency: 3,
        maxChunkRetry: 2,
    },
    TXT: {
        maxSizeByte: 10 * MB,
        allowedExtensions: ['txt', 'doc', 'docx', 'xlsx'],
        maxCount: 1,
        multiple: false,
        concurrency: 1,
        customUrl: '',
        transport: 'direct',
        chunkThreshold: 10 * MB,
        chunkSize: 5 * MB,
        chunkConcurrency: 3,
        maxChunkRetry: 2,
    },
};

export const DEFAULT_AUTO_UPLOAD_CONFIG: Required<UploadOptions> = {
    maxSizeByte: {
        image: 10 * MB,
        video: 500 * MB,
        audio: 50 * MB,
        txt: 10 * MB,
    },
    allowedExtensions: [
        'jpg',
        'jpeg',
        'png',
        'webp',
        'mp4',
        'mov',
        'avi',
        'mkv',
        'webm',
        'mp3',
        'wav',
        'txt',
        'doc',
        'docx',
        'xlsx',
    ],
    maxCount: 1,
    multiple: false,
    concurrency: 1,
    customUrl: '',
    transport: 'auto',
    chunkThreshold: 10 * MB,
    chunkSize: 5 * MB,
    chunkConcurrency: 3,
    maxChunkRetry: 2,
};

// ============================================================
// 配置合并与文件类型推断（hook / hostApi 共用）
// ============================================================

/**
 * 合并默认配置与用户配置。
 * type='AUTO' 时使用 DEFAULT_AUTO_UPLOAD_CONFIG，否则使用对应类型的 DEFAULT_UPLOAD_CONFIGS[type]。
 */
export function mergeUploadOptions(
    type: UploadFileTypeOption,
    userOptions?: UploadOptions,
): Required<UploadOptions> {
    return {
        ...(type === 'AUTO' ? DEFAULT_AUTO_UPLOAD_CONFIG : DEFAULT_UPLOAD_CONFIGS[type]),
        ...userOptions,
    };
}

/**
 * 根据入参 type 与单个文件，解析出最终上传所需的 fileType 与 options。
 * AUTO 时按 MIME 推断具体 fileType，并使用该具体类型的默认配置作为底；非 AUTO 时直接合并。
 */
export function resolveUploadParams(
    file: File,
    type: UploadFileTypeOption,
    userOptions?: UploadOptions,
): {fileType: UploadFileType; options: Required<UploadOptions>} {
    const resolvedType = type === 'AUTO' ? inferUploadFileType(file) : type;
    if (!resolvedType) {
        throw new UploadError('VALIDATE', 'FILE_TYPE_INVALID');
    }
    const options: Required<UploadOptions> =
        type === 'AUTO'
            ? {...DEFAULT_UPLOAD_CONFIGS[resolvedType], ...userOptions}
            : mergeUploadOptions(type, userOptions);
    return {fileType: resolvedType, options};
}

// ============================================================
// 工具函数
// ============================================================

function sliceFile(file: File, chunkSize: number): Blob[] {
    const chunks: Blob[] = [];
    let offset = 0;
    while (offset < file.size) {
        chunks.push(file.slice(offset, offset + chunkSize));
        offset += chunkSize;
    }
    return chunks;
}

function shouldUseChunk(file: File, options: Required<UploadOptions>): boolean {
    if (options.transport === 'direct') return false;
    if (options.transport === 'chunk') return true;
    // auto: 文件大小超过阈值则走分片
    return file.size >= options.chunkThreshold;
}

function toProtocolFileType(fileType: UploadFileType): string {
    return FILE_TYPE_PROTOCOL_MAP[fileType];
}

function createRiskBlockedError(reason?: string) {
    const error = new UploadError('RISK_CHECK', 'RISK_BLOCKED');
    error.message = reason ?? error.message;
    return error;
}

function createAbortError() {
    return new UploadError('UPLOAD', 'UPLOAD_ABORTED');
}

function createUploadErrorWithServerMessage(
    stage: ConstructorParameters<typeof UploadError>[0],
    status: ConstructorParameters<typeof UploadError>[1],
    raw: unknown,
) {
    const error = new UploadError(stage, status, raw);
    if (raw instanceof RequestError && raw.message) {
        error.message = raw.message;
    }
    return error;
}

async function pollRiskResult(response: UploadResult, signal?: AbortSignal): Promise<UploadResult> {
    if (!response.taskId) {
        throw new UploadError('UPLOAD', 'UPLOAD_RESPONSE_INVALID');
    }
    const taskId = response.taskId;

    if (signal?.aborted) {
        throw createAbortError();
    }

    return new Promise<UploadResult>((resolve, reject) => {
        let settled = false;
        let abortHandler: (() => void) | null = null;

        const manager = new PollingManager<RiskStatusResult>({
            queryFn: async (taskIds) => {
                const currentTaskId = taskIds[0];
                if (!currentTaskId) {
                    return {};
                }

                const result = await queryRiskStatus({taskId: currentTaskId}, {signal});
                return {[currentTaskId]: result};
            },
            interval: RISK_POLLING_INTERVAL,
            maxRetries: RISK_POLLING_MAX_RETRIES,
        });

        const cleanup = () => {
            if (abortHandler) {
                signal?.removeEventListener('abort', abortHandler);
            }
            manager.destroy();
        };

        const finish = (fn: () => void) => {
            if (settled) {
                return;
            }

            settled = true;
            cleanup();
            fn();
        };

        abortHandler = () => {
            finish(() => reject(createAbortError()));
        };

        signal?.addEventListener('abort', abortHandler, {once: true});

        manager.add(taskId, {
            shouldContinue: (result) => result.riskStatus === 'processing',
            onSettled: (result) => {
                finish(() => {
                    if (result.riskStatus === 'pass') {
                        if (!result.fileUrl) {
                            reject(new UploadError('UPLOAD', 'UPLOAD_RESPONSE_INVALID'));
                            return;
                        }

                        resolve({
                            ...response,
                            ...result,
                            riskStatus: 'pass',
                            coverUrl: response.coverUrl,
                        });
                        return;
                    }

                    if (result.riskStatus === 'blocked') {
                        reject(createRiskBlockedError(result.reason));
                        return;
                    }

                    reject(new UploadError('RISK_CHECK', 'RISK_TIMEOUT'));
                });
            },
            onError: (error) => {
                finish(() => reject(new UploadError('RISK_CHECK', 'RISK_TIMEOUT', error)));
            },
        });
    });
}

export async function resolveUploadRisk(
    response: UploadResult,
    onRiskProcessing?: (response: UploadResult) => void,
    signal?: AbortSignal,
): Promise<UploadResult> {
    if (!response.riskStatus && response.fileUrl) {
        return {...response, riskStatus: 'pass'};
    }

    if (response.riskStatus === 'pass') {
        return response;
    }

    if (response.riskStatus === 'blocked') {
        throw createRiskBlockedError(response.reason);
    }

    if (!response.taskId) {
        throw new UploadError('UPLOAD', 'UPLOAD_RESPONSE_INVALID');
    }

    onRiskProcessing?.(response);
    return pollRiskResult(response, signal);
}

// ============================================================
// API 调用
// ============================================================

/** 直传单个文件 */
export async function directUpload(
    file: File,
    options: UploadOptions,
    scene: UploadSource,
    fileType: UploadFileType,
    projectId?: ProjectId,
    headers?: Record<string, string>,
    signal?: AbortSignal,
): Promise<UploadResult> {
    const formData = new FormData();
    const maxSizeByte = resolveMaxSizeByte(file, options.maxSizeByte, fileType);
    formData.append('file', file);
    formData.append('fileType', toProtocolFileType(fileType));
    formData.append('fileName', file.name);
    formData.append('fileSize', String(file.size));
    formData.append('maxSizeByte', String(maxSizeByte ?? 0));
    formData.append('scene', scene);
    if (projectId) {
        formData.append('projectId', projectId);
    }
    if (options.allowedExtensions) {
        for (const ext of options.allowedExtensions) {
            formData.append('allowedExtensions', ext);
        }
    }

    try {
        return await directUploadFile(formData, {signal, headers});
    } catch (err) {
        if (err instanceof UploadError) throw err;
        throw createUploadErrorWithServerMessage('UPLOAD', 'UPLOAD_REQUEST_FAILED', err);
    }
}

/** 获取分片上传 ID */
async function getChunkUploadId(
    fileName: string,
    scene: UploadSource,
    headers?: Record<string, string>,
    signal?: AbortSignal,
): Promise<ChunkInitData> {
    try {
        return await getUploadId({fileName, scene}, {signal, headers});
    } catch (err) {
        throw createUploadErrorWithServerMessage('CHUNK_INIT', 'UPLOAD_INIT_FAILED', err);
    }
}

/** 上传单个分片 */
async function uploadSingleChunk(
    params: {uploadId: string; key: string; file: Blob; number: number; total: number},
    headers?: Record<string, string>,
    signal?: AbortSignal,
): Promise<string> {
    const formData = new FormData();
    formData.append('uploadId', params.uploadId);
    formData.append('key', params.key);
    formData.append('file', params.file);
    formData.append('number', String(params.number));
    formData.append('total', String(params.total));

    const res = await uploadChunkPart(formData, {signal, headers});
    return res.etag;
}

/** 单个分片带重试上传 */
async function uploadChunkWithRetry(
    params: {uploadId: string; key: string; file: Blob; number: number; total: number},
    maxRetry: number,
    headers?: Record<string, string>,
    signal?: AbortSignal,
): Promise<string> {
    let lastError: unknown = null;
    for (let attempt = 0; attempt <= maxRetry; attempt++) {
        try {
            return await uploadSingleChunk(params, headers, signal);
        } catch (err) {
            lastError = err;
            if (signal?.aborted) break;
        }
    }
    throw createUploadErrorWithServerMessage('CHUNK_UPLOAD', 'CHUNK_UPLOAD_FAILED', lastError);
}

/** 合并分片 */
async function mergeChunks(
    params: {
        uploadId: string;
        key: string;
        total: number;
        etags: string[];
        scene: UploadSource;
        fileType: UploadFileType;
        fileSize: number; // 分片server拿不到文件大小，上传辅助校验。
        projectId?: ProjectId;
    },
    headers?: Record<string, string>,
    signal?: AbortSignal,
): Promise<UploadResult> {
    try {
        return await mergeChunkParts(
            {...params, fileType: toProtocolFileType(params.fileType)},
            {signal, headers},
        );
    } catch (err) {
        throw createUploadErrorWithServerMessage('CHUNK_MERGE', 'CHUNK_MERGE_FAILED', err);
    }
}

/** 分片上传完整流程 */
export async function chunkUpload(
    file: File,
    options: UploadOptions,
    scene: UploadSource,
    fileType: UploadFileType,
    projectId?: ProjectId,
    onProgress?: (completed: number, total: number) => void,
    headers?: Record<string, string>,
    signal?: AbortSignal,
): Promise<UploadResult> {
    const chunkSize = options.chunkSize ?? 5 * MB;
    const concurrency = options.chunkConcurrency ?? 3;
    const maxRetry = options.maxChunkRetry ?? 2;

    // 1. 获取 uploadId
    const {uploadId, key} = await getChunkUploadId(file.name, scene, headers, signal);

    // 2. 切片
    const chunks = sliceFile(file, chunkSize);
    const total = chunks.length;
    const etags: string[] = new Array<string>(total);
    let completed = 0;

    // 3. 并发上传分片（失败时取消剩余）
    const chunkAbort = new AbortController();
    const onParentAbort = () => chunkAbort.abort();
    signal?.addEventListener('abort', onParentAbort);

    try {
        let nextIndex = 0;

        const worker = async () => {
            while (nextIndex < total) {
                if (chunkAbort.signal.aborted) return;
                const i = nextIndex++;
                etags[i] = await uploadChunkWithRetry(
                    {uploadId, key, file: chunks[i], number: i + 1, total},
                    maxRetry,
                    headers,
                    chunkAbort.signal,
                );
                completed++;
                onProgress?.(completed, total);
            }
        };

        await Promise.all(Array.from({length: Math.min(concurrency, total)}, () => worker()));
    } catch (err) {
        chunkAbort.abort();
        throw err;
    } finally {
        signal?.removeEventListener('abort', onParentAbort);
    }

    // 4. 合并
    return await mergeChunks(
        {uploadId, key, total, etags, scene, fileType, fileSize: file.size, projectId},
        headers,
        signal,
    );
}

// ============================================================
// 统一上传入口
// ============================================================

export interface UploadFileParams {
    file: File;
    options: Required<UploadOptions>;
    scene: UploadSource;
    fileType: UploadFileType;
    projectId?: ProjectId;
    headers?: Record<string, string>;
    onProgress?: (completed: number, total: number) => void;
    onRiskProcessing?: (response: UploadResult) => void;
    signal?: AbortSignal;
}

/**
 * 上传单个文件
 *
 * 根据 transport 和文件大小自动选择直传或分片上传。
 * 返回统一的 UploadResult，包含风控状态。
 */
export async function uploadFile(params: UploadFileParams): Promise<UploadResult> {
    const {
        file,
        options,
        scene,
        fileType,
        projectId,
        headers,
        onProgress,
        onRiskProcessing,
        signal,
    } = params;

    if (shouldUseChunk(file, options)) {
        const response = await chunkUpload(
            file,
            options,
            scene,
            fileType,
            projectId,
            onProgress,
            headers,
            signal,
        );
        return resolveUploadRisk(response, onRiskProcessing, signal);
    }

    const response = await directUpload(file, options, scene, fileType, projectId, headers, signal);
    return resolveUploadRisk(response, onRiskProcessing, signal);
}
